<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.home-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Welcome') }}
          </h1>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="my-3 md:flex md:space-x-4">
        <div class="w-full md:w-1/2 bg-white p-4 border-t-2 border-green-600 bg-white shadow rounded-md">
          <loading :status="loading"></loading>
          <form @submit.prevent="findUp">
            <label for="default-search" class="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-gray-300">{{ $t('Search') }}</label>
            <div class="relative">
              <div class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                <svg aria-hidden="true" class="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <input
                type="text"
                id="default-search"
                class="block p-4 pl-10 w-full text-sm text-gray-900 bg-green-50 rounded-lg border border-green-300 focus:ring-green-500 focus:border-green-500 form-input"
                :placeholder="$t('Search all invoices')"
                required
                v-model="search"
              />
              <button
                type="submit"
                v-if="search"
                class="text-white absolute right-2.5 bottom-2.5 bg-green-700 hover:bg-green-800 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-4 py-2"
              >
                {{ $t('Search') }}
              </button>
            </div>
          </form>
          <template v-if="searchedData.sale_invoices.length">
            <div class="border-l-2 border-green-500 rounded-md text-xs mt-3">
              <strong class="px-2 py-1 w-full bg-yellow-200 ml-1 rounded-md">{{ $t('Sale orders') }} ({{ searchedData.sale_invoices.length }})</strong>
              <ul class="bg-white rounded-lg text-gray-900 p-1">
                <router-link
                  tag="li"
                  v-for="(sale, skey) in searchedData.sale_invoices"
                  :key="skey"
                  class="px-4 py-1 border-t border-gray-200 w-full rounded-t-lg cursor-pointer hover:bg-gray-100 hover:text-sm"
                  :to="`/print/sale-order/${sale.uuid}`"
                >
                  <p class="flex justify-between">
                    <span>{{ $t('Sale order ') }} #{{ sale.tracking }}</span>
                    {{ sale.payable_after_all | currency }}
                  </p>
                  <small>{{ sale.created_at }}</small>
                </router-link>
              </ul>
            </div>
          </template>
          <template v-if="searchedData.refund_invoices.length">
            <div class="border-l-2 border-green-500 rounded-md text-xs mt-3">
              <strong class="px-2 py-1 w-full bg-yellow-200 ml-1 rounded-md">{{ $t('Sale return orders') }} ({{ searchedData.refund_invoices.length }})</strong>
              <ul class="bg-white rounded-lg text-gray-900 p-1">
                <router-link
                  tag="li"
                  v-for="(refund, skey) in searchedData.refund_invoices"
                  :key="skey"
                  class="px-4 py-1 border-t border-gray-200 w-full rounded-t-lg cursor-pointer hover:bg-gray-100 hover:text-sm"
                  :to="`/print/refund-order/${refund.uuid}`"
                >
                  <p class="flex justify-between">
                    <span>{{ $t('Refund order ') }} #{{ refund.tracking }}</span>
                    {{ refund.payable_after_all | currency }}
                  </p>
                  <small>{{ refund.created_at }}</small>
                </router-link>
              </ul>
            </div>
          </template>
          <template v-if="searchedData.custom_products_invoices.length">
            <div class="border-l-2 border-blue-500 rounded-md text-xs mt-3">
              <strong class="px-2 py-1 w-full bg-yellow-200 ml-1 rounded-md"
                >{{ $t('Custom products purchased by customers') }} ({{ searchedData.custom_products_invoices.length }})</strong
              >
              <ul class="bg-white rounded-lg text-gray-900 p-1">
                <router-link
                  tag="li"
                  v-for="(product, skey) in searchedData.custom_products_invoices"
                  :key="skey"
                  class="px-4 py-1 border-t border-gray-200 w-full rounded-t-lg cursor-pointer hover:bg-gray-100 hover:text-sm"
                  :to="`/print/custom-product/${product.uuid}`"
                >
                  <p class="flex justify-between">
                    <span>{{ $t('Product purchased order ') }} #{{ product.tracking }}</span>
                    {{ product.full_name }} {{ product.cost | currency }}
                  </p>
                  <small>{{ product.created_at }}</small>
                </router-link>
              </ul>
            </div>
          </template>
          <template v-if="searchedData.bill_invoices.length">
            <div class="border-l-2 border-red-500 rounded-md text-xs mt-3">
              <strong class="px-2 py-1 w-full bg-yellow-200 ml-1 rounded-md">{{ $t('Custom bills') }} ({{ searchedData.bill_invoices.length }})</strong>
              <ul class="bg-white rounded-lg text-gray-900 p-1">
                <router-link
                  tag="li"
                  v-for="(bill, bkey) in searchedData.bill_invoices"
                  :key="bkey"
                  class="px-4 py-1 border-t border-gray-200 w-full rounded-t-lg cursor-pointer hover:bg-gray-100 hover:text-sm"
                  :to="`/print/custom-bill/${bill.uuid}`"
                >
                  <p class="flex justify-between">
                    <span>{{ $t('Bill tracking') }} #{{ bill.tracking }}</span>
                    {{ bill.name }} {{ bill.email }}
                  </p>
                  <small>{{ bill.created_at }}</small>
                </router-link>
              </ul>
            </div>
          </template>
          <template v-if="searchedData.repair_invoices.length">
            <div class="border-l-2 border-pink-500 rounded-md text-xs mt-3">
              <strong class="px-2 py-1 w-full bg-yellow-200 ml-1 rounded-md">{{ $t('Repair orders') }} ({{ searchedData.repair_invoices.length }})</strong>
              <ul class="bg-white rounded-lg text-gray-900 p-1">
                <router-link
                  tag="li"
                  v-for="(repair, rkey) in searchedData.repair_invoices"
                  :key="rkey"
                  class="px-4 py-1 border-t border-gray-200 w-full rounded-t-lg cursor-pointer hover:bg-gray-100 hover:text-sm"
                  :to="`/print/repair/${repair.uuid}`"
                >
                  <p class="flex justify-between">
                    <span>{{ $t('Repair order ') }} #{{ repair.tracking }}</span>
                    {{ repair.device_name }} {{ repair.name }} {{ repair.charges | currency }}
                  </p>
                  <small>{{ repair.created_at }}</small>
                </router-link>
              </ul>
            </div>
          </template>
        </div>
        <div class="w-full md:w-1/2">
          <ul class="grid grid-cols-3 gap-2">
            <router-link class="col-span-1 flex flex-col text-center cursor-pointer border-t-2 border-green-600 bg-white shadow rounded-md" tag="li" to="/pos">
              <div class="flex-1 flex flex-col p-5">
                <div class="flex-shrink-0 mx-auto">
                  <svg-vue class="block-link-icon" icon="font-awesome.life-ring-solid"></svg-vue>
                </div>
                <h3 class="form-description-head">
                  {{ $t('Point of sale') }}
                </h3>
                <p class="form-description-text">
                  {{ $t('Sale items and accessories') }}
                </p>
              </div>
            </router-link>
            <router-link class="col-span-1 flex flex-col text-center cursor-pointer border-t-2 border-green-600 bg-white shadow rounded-md" tag="li" to="/custom-bill">
              <div class="flex-1 flex flex-col p-5">
                <div class="flex-shrink-0 mx-auto">
                  <svg-vue class="block-link-icon" icon="font-awesome.plus-circle-solid"></svg-vue>
                </div>
                <h3 class="form-description-head">
                  {{ $t('Custom bill') }}
                </h3>
                <p class="form-description-text">
                  {{ $t('Create custom quick bill') }}
                </p>
              </div>
            </router-link>
            <router-link class="col-span-1 flex flex-col text-center cursor-pointer border-t-2 border-green-600 bg-white shadow rounded-md" tag="li" to="/repair-booking">
              <div class="flex-1 flex flex-col p-5">
                <div class="flex-shrink-0 mx-auto">
                  <svg-vue class="block-link-icon" icon="font-awesome.file-invoice-solid"></svg-vue>
                </div>
                <h3 class="form-description-head">
                  {{ $t('Book repair order') }}
                </h3>
                <p class="form-description-text">
                  {{ $t('Repair order booking for repairing') }}
                </p>
              </div>
            </router-link>
            <router-link class="col-span-1 flex flex-col text-center cursor-pointer border-t-2 border-green-600 bg-white shadow rounded-md" tag="li" to="/custom-product">
              <div class="flex-1 flex flex-col p-5">
                <div class="flex-shrink-0 mx-auto">
                  <svg-vue class="block-link-icon" icon="font-awesome.file-invoice-dollar-solid"></svg-vue>
                </div>
                <h3 class="form-description-head">
                  {{ $t('Buy from customer') }}
                </h3>
                <p class="form-description-text">
                  {{ $t('Buy product form walk in customer for reselling') }}
                </p>
              </div>
            </router-link>
              <router-link class="col-span-1 flex flex-col text-center cursor-pointer border-t-2 border-green-600 bg-white shadow rounded-md" tag="li" to="/cashbook-close">
              <div class="flex-1 flex flex-col p-5">
                <div class="flex-shrink-0 mx-auto">
                  <svg-vue class="block-link-icon" icon="font-awesome.times-circle-light"></svg-vue>
                </div>
                <h3 class="form-description-head">
                  {{ $t('Z out') }}
                </h3>
                <p class="form-description-text">
                  {{ $t('Prepair cash register to be closed') }}
                </p>
              </div>
            </router-link>
          </ul>
        </div>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: 'home',
  metaInfo() {
    return {
      title: this.$i18n.t('Welcome'),
    };
  },
  mounted() {
    if (this.$route.query.search) {
      this.search = this.$route.query.search;
      this.findUp();
    }
  },
  data() {
    return {
      loading: false,
      search: null,
      searchedData: {
        bill_invoices: [],
        custom_products_invoices: [],
        repair_invoices: [],
        sale_invoices: [],
        refund_invoices: [],
      },
    };
  },
  methods: {
    findUp() {
      this.loading = true;
      this.$router.push({ path: this.$route.path, query: { search: this.search } });
      axios
        .post(route('search.data', { search: this.search }))
        .then((response) => {
          this.searchedData = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
